import {
  BRAND_TONES,
  PAYMENT_METHODS,
  SERVICE_AREA_KINDS,
  type BrandClosure,
  type BrandImport,
  type BrandLink,
  type BrandProfile,
  type BrandService,
  type OpeningHours,
  type PaymentMethod,
  type ServiceArea,
} from '@unipostudio/shared-types';
import { api } from './api';

/**
 * The brand profile (blueprint S04): the single source of truth for AI
 * replies, posters and occasions.
 *
 *   GET   /workspaces/me/brand-profile          → BrandProfile (defaults when nothing is stored)
 *   PATCH /workspaces/me/brand-profile          { patch, baseUpdatedAt } → BrandProfile
 *   POST  /workspaces/me/brand-profile/import   { url } | { platform } → BrandImport (nothing saved)
 *
 * One request per autosave (AL-42): the server merges the changed fields,
 * writes the brand's own columns (name, logo, industry, timezone, content
 * language, website) in the same request, and answers 409 PROFILE_CONFLICT
 * rather than overwrite a teammate's newer save.
 */

const PROFILE_URL = '/workspaces/me/brand-profile';
const IMPORT_URL = `${PROFILE_URL}/import`;

// ── The server's rules (apps/api/src/modules/brand-profile/brand-profile.rules.ts) ──
// The editor autosaves 400 ms after each keystroke, so a half-typed email or
// colour reaches saveProfile. The server refuses the whole document for one
// bad field, which would stop every other field from saving too; instead the
// last saved value is sent for a field that does not pass yet. The editor
// keeps showing what was typed, with its inline error, until it is fixed.

const LIMITS = {
  name: 120,
  tagline: 200,
  businessType: 80,
  industry: 80,
  region: 64,
  city: 120,
  address: 300,
  phone: 40,
  email: 254,
  url: 2048,
  policies: 5000,
  audience: 2000,
  hours: 7,
  closures: 60,
  closureLabel: 120,
  serviceAreas: 40,
  serviceAreaName: 120,
  serviceAreaNotes: 1000,
  services: 100,
  serviceId: 64,
  serviceName: 120,
  maxPrice: 1_000_000_000,
  links: 20,
  linkLabel: 60,
  languages: 10,
} as const;

const HEX_COLOUR = /^#[0-9a-f]{6}$/i;
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;
const ISO_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const COUNTRY_CODE = /^[A-Z]{2}$/;
const CURRENCY_CODE = /^[A-Z]{3}$/;
const LANGUAGE_TAG = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8}){0,3}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > LIMITS.url) return false;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname !== '';
  } catch {
    return false;
  }
}

function isTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

const isText =
  (max: number) =>
  (v: unknown): v is string =>
    typeof v === 'string' && v.length <= max;
const isNullableText =
  (max: number) =>
  (v: unknown): boolean =>
    v === null || isText(max)(v);
const isUrlOrEmpty = (v: unknown): boolean => v === '' || isHttpUrl(v);
const isNullableUrl = (v: unknown): boolean => v === null || isHttpUrl(v);
const matches =
  (re: RegExp) =>
  (v: unknown): boolean =>
    typeof v === 'string' && re.test(v);

function isHours(value: unknown): value is OpeningHours[] {
  if (!Array.isArray(value) || value.length > LIMITS.hours) return false;
  const seen = new Set<number>();
  return value.every((h: Partial<OpeningHours> | null) => {
    if (!h || typeof h.weekday !== 'number' || !Number.isInteger(h.weekday) || h.weekday < 0 || h.weekday > 6 || seen.has(h.weekday)) return false;
    seen.add(h.weekday);
    const open = h.open ?? null;
    const close = h.close ?? null;
    if (open === null || close === null) return open === null && close === null && h.open === null && h.close === null;
    return HH_MM.test(open) && HH_MM.test(close);
  });
}

/** Mirrors the server's BrandClosureDto: an ISO date, and either both times or neither. */
function isClosures(value: unknown): value is BrandClosure[] {
  if (!Array.isArray(value) || value.length > LIMITS.closures) return false;
  const seen = new Set<string>();
  return value.every((c: Partial<BrandClosure> | null) => {
    if (!c || typeof c.date !== 'string' || !ISO_DATE.test(c.date) || seen.has(c.date)) return false;
    seen.add(c.date);
    if (typeof c.label !== 'string' || c.label.length > LIMITS.closureLabel) return false;
    const open = c.open ?? null;
    const close = c.close ?? null;
    if (open === null || close === null) return open === null && close === null;
    return HH_MM.test(open) && HH_MM.test(close);
  });
}

function isPaymentMethods(value: unknown): value is PaymentMethod[] {
  return (
    Array.isArray(value) &&
    value.length <= PAYMENT_METHODS.length &&
    new Set(value).size === value.length &&
    value.every((m) => (PAYMENT_METHODS as readonly unknown[]).includes(m))
  );
}

function isServiceArea(value: unknown): value is ServiceArea {
  if (!value || typeof value !== 'object') return false;
  const a = value as Partial<ServiceArea>;
  return (
    (SERVICE_AREA_KINDS as readonly unknown[]).includes(a.kind) &&
    Array.isArray(a.areas) &&
    a.areas.length <= LIMITS.serviceAreas &&
    a.areas.every((x) => typeof x === 'string' && x.length <= LIMITS.serviceAreaName) &&
    typeof a.notes === 'string' &&
    a.notes.length <= LIMITS.serviceAreaNotes
  );
}

/** Prices keep two decimals, as the server stores them; NaN from an emptied input is no price. */
function normaliseServices(value: unknown): BrandService[] | null {
  if (!Array.isArray(value) || value.length > LIMITS.services) return null;
  const out: BrandService[] = [];
  for (const s of value as Partial<BrandService>[]) {
    if (!s || typeof s !== 'object') return null;
    const price = typeof s.price === 'number' && Number.isFinite(s.price) ? Math.round(s.price * 100) / 100 : null;
    if (
      typeof s.id !== 'string' ||
      s.id.length === 0 ||
      s.id.length > LIMITS.serviceId ||
      !isText(LIMITS.serviceName)(s.name) ||
      (price !== null && (price < 0 || price > LIMITS.maxPrice)) ||
      !matches(CURRENCY_CODE)(s.currency) ||
      typeof s.inPromotions !== 'boolean'
    ) {
      return null;
    }
    out.push({ id: s.id, name: s.name, price, currency: s.currency as string, inPromotions: s.inPromotions });
  }
  return out;
}

/** A half-typed link stays in the editor and is left out of the save until its URL is whole. */
function persistableLinks(value: unknown): BrandLink[] | null {
  if (!Array.isArray(value)) return null;
  const links = (value as Partial<BrandLink>[])
    .filter((l): l is BrandLink => !!l && isText(LIMITS.linkLabel)(l.label) && isUrlOrEmpty(l.url))
    .map((l) => ({ label: l.label, url: l.url }));
  return links.length <= LIMITS.links ? links : null;
}

function isLanguages(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= 1 &&
    value.length <= LIMITS.languages &&
    new Set(value).size === value.length &&
    value.every((l) => typeof l === 'string' && LANGUAGE_TAG.test(l))
  );
}

/**
 * The document PUT sends: `next` field by field, with the last saved value
 * (`current`) wherever `next` would not pass the server's validation. Only
 * BrandProfile keys are sent; the server rejects any other.
 */
export function persistableProfile(current: BrandProfile, next: BrandProfile): BrandProfile {
  const keep = <K extends keyof BrandProfile>(key: K, ok: (v: unknown) => boolean): BrandProfile[K] => (ok(next[key]) ? next[key] : current[key]);
  return {
    name: keep('name', isText(LIMITS.name)),
    tagline: keep('tagline', isText(LIMITS.tagline)),
    logoUrl: keep('logoUrl', isNullableUrl),
    logoLightUrl: keep('logoLightUrl', isNullableUrl),
    accent: keep('accent', matches(HEX_COLOUR)),
    secondary: keep('secondary', matches(HEX_COLOUR)),
    businessType: keep('businessType', isText(LIMITS.businessType)),
    industry: keep('industry', isNullableText(LIMITS.industry)),
    country: keep('country', matches(COUNTRY_CODE)),
    region: keep('region', isNullableText(LIMITS.region)),
    city: keep('city', isText(LIMITS.city)),
    address: keep('address', isText(LIMITS.address)),
    phone: keep('phone', isText(LIMITS.phone)),
    email: keep('email', (v) => isText(LIMITS.email)(v) && (v === '' || EMAIL.test(v))),
    website: keep('website', isUrlOrEmpty),
    bookingUrl: keep('bookingUrl', isUrlOrEmpty),
    timezone: keep('timezone', isTimeZone),
    hours: keep('hours', isHours),
    closures: keep('closures', isClosures),
    services: normaliseServices(next.services) ?? current.services,
    currency: keep('currency', matches(CURRENCY_CODE)),
    paymentMethods: keep('paymentMethods', isPaymentMethods),
    serviceArea: keep('serviceArea', isServiceArea),
    tone: keep('tone', (v) => (BRAND_TONES as readonly unknown[]).includes(v)),
    languages: keep('languages', isLanguages),
    policies: keep('policies', isText(LIMITS.policies)),
    links: persistableLinks(next.links) ?? current.links,
    audience: keep('audience', isText(LIMITS.audience)),
    updatedAt: current.updatedAt,
  };
}

export const brandService = {
  async getProfile(): Promise<BrandProfile> {
    const res = await api.get<BrandProfile>(PROFILE_URL);
    return res.data;
  },

  /**
   * Saves a patch in one request (AL-42). `base` is the profile as the server
   * last confirmed it: a half-typed field falls back to its value, and its
   * `updatedAt` lets the server refuse to overwrite a newer save. Without a
   * base the stored profile is read first.
   */
  async saveProfile(patch: Partial<BrandProfile>, base?: BrandProfile): Promise<BrandProfile> {
    const current = base ?? (await brandService.getProfile());
    const next = persistableProfile(current, { ...current, ...patch });
    const keys = Object.keys(patch).filter((key) => key !== 'updatedAt' && key in next) as (keyof BrandProfile)[];
    const accepted = Object.fromEntries(keys.map((key) => [key, next[key]])) as Partial<BrandProfile>;
    const res = await api.patch<BrandProfile>(PROFILE_URL, {
      patch: accepted,
      ...(current.updatedAt ? { baseUpdatedAt: current.updatedAt } : {}),
    });
    return res.data;
  },

  /** Reads the site server-side. Nothing is saved: the person ticks fields in the review sheet first. */
  async importFromWebsite(url: string): Promise<BrandImport> {
    const res = await api.post<BrandImport>(IMPORT_URL, { url });
    return res.data;
  },

  /** Reads what was stored when the channel was connected (name, handle, picture); no live platform call. */
  async importFromChannel(platform: string): Promise<BrandImport> {
    const res = await api.post<BrandImport>(IMPORT_URL, { platform });
    return res.data;
  },
};
